// src/components/resources/tools/generators/FakeChat/IosStatusBar.tsx

'use client'

import { cn } from '~/lib/utils'

/**
 * Shared iOS status bar (clock + cellular/Wi-Fi/battery) for the phone-framed
 * chat previews (WhatsApp, Messenger). Rendering this above each app header is
 * what keeps the header content clear of the Dynamic Island notch — the notch
 * sits centred between the corner-anchored clock and battery, exactly as on a
 * real device. `tone` flips the glyph colour for light headers (WhatsApp's
 * teal bar → white) vs dark-on-light headers (Messenger's white bar → black).
 *
 * The iMessage preview keeps its own inline status bar (it predates this
 * helper and is tuned to that layout); this component covers the two newer
 * phone previews so their notch clearance and spacing stay consistent.
 */
export function IosStatusBar({ time, tone }: { time: string; tone: 'light' | 'dark' }): JSX.Element {
  const glyph = tone === 'light' ? '#ffffff' : '#000000'
  const textColor = tone === 'light' ? 'text-white' : 'text-black'

  return (
    // Fixed h-[50px] reserves the full Dynamic Island safe-area row: the notch
    // pill spans ~12–40px, so a clock-height-only bar would let the app header
    // start at ~32px and collide with the island. With a 50px bar the clock
    // vertically centres beside the island (centres at ~25px vs the island's
    // ~26px) and the header below starts at 50px — ~10px clear of the pill,
    // matching how native iOS seats the nav bar beneath the status area.
    <div className={cn('relative z-10 flex h-[50px] select-none items-center justify-between px-6 text-[14px] font-semibold', textColor)}>
      <span>{time}</span>
      <div className="flex items-center gap-1.5">
        {/* Cellular bars */}
        <span className="flex items-end gap-0.5" aria-hidden>
          <span className="h-1.5 w-0.5 rounded-full" style={{ background: glyph }} />
          <span className="h-2 w-0.5 rounded-full" style={{ background: glyph }} />
          <span className="h-2.5 w-0.5 rounded-full" style={{ background: glyph }} />
          <span className="h-3 w-0.5 rounded-full" style={{ background: glyph }} />
        </span>
        {/* Wi-Fi */}
        <svg width="16" height="12" viewBox="0 0 16 12" fill="none" aria-hidden>
          <path d="M8 0C4.13 0 0.74 1.86 0 4.15l1.04 1.04C1.55 3.34 4.46 1.5 8 1.5c3.54 0 6.45 1.84 6.96 3.69L16 4.15C15.26 1.86 11.87 0 8 0Z" fill={glyph} />
          <path d="M8 3C5.5 3 3.34 4.35 2.75 5.75l1.01 1.01C4.21 5.6 5.94 4.5 8 4.5s3.79 1.1 4.24 2.26l1.01-1.01C12.66 4.35 10.5 3 8 3Z" fill={glyph} />
          <path d="M8 6C6.87 6 5.91 6.54 5.5 7.25l1.05 1.05C6.77 7.96 7.33 7.5 8 7.5s1.23 0.46 1.45 0.8l1.05-1.05C10.09 6.54 9.13 6 8 6Z" fill={glyph} />
          <circle cx="8" cy="9.5" r="1.2" fill={glyph} />
        </svg>
        {/* Battery */}
        <svg width="24" height="12" viewBox="0 0 25 12" fill="none" aria-hidden>
          <rect x="1" y="1" width="19" height="8" rx="2" stroke={glyph} strokeWidth="1.5" />
          <rect x="21" y="3" width="1.5" height="4" rx="0.75" fill={glyph} />
          <rect x="2" y="2" width="17" height="6" rx="1" fill="#34C759" />
        </svg>
      </div>
    </div>
  )
}

/**
 * Derive an iOS-style clock string ("9:41") from a chat timestamp such as
 * "9:24 AM", stripping the meridiem. Falls back to the canonical 9:41.
 */
export function statusBarTimeFrom(timestamp: string | undefined): string {
  if (!timestamp) return '9:41'
  return timestamp.replace(/\s?(AM|PM)$/i, '').trim() || '9:41'
}
